import React, { useState, useEffect } from 'react';
import { ExternalLink, BookOpen, Award, Search, Globe, Mail, Check, XCircle } from 'lucide-react';
import axios from 'axios';

const Revistas = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Agregar cache busting con timestamp
        const response = await axios.get(`/data/revistas.json?v=${Date.now()}`);

        console.log('✅ Datos de revistas cargados:', response.data);

        // Validar que la respuesta tenga la estructura esperada
        if (!response.data || !response.data.revistas) {
          throw new Error('Estructura de datos inválida en revistas.json');
        }

        setData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error cargando datos de revistas:', err);
        console.error('Detalles del error:', {
          message: err.message,
          response: err.response,
          status: err.response?.status
        });
        setError(`Error al cargar los datos: ${err.message}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Mapeo de iconos para las características
  const iconMap = {
    Award,
    Search,
    Globe,
    BookOpen,
    Mail
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0b742f] mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando revistas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Error al cargar datos</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  if (!data) return null;

  // Filtrar solo revistas activas
  const revistasActivas = data.revistas.filter(revista => revista.activa);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <BookOpen className="w-12 h-12 text-[#e87729]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                {data.hero.titulo}
              </h1>
              <p className="text-xl text-green-100 mt-2">
                {data.hero.subtitulo}
              </p>
            </div>
          </div>
          <p className="text-lg text-green-100 max-w-4xl">
            {data.hero.descripcion}
          </p>
        </div>
      </section>

      {/* Características de las Revistas */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Award className="w-8 h-8 text-[#e87729]" />
                Características de Nuestras Revistas
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {data.caracteristicas.map((caracteristica, index) => {
                  const IconComponent = iconMap[caracteristica.icono];
                  return (
                    <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 p-6 hover:shadow-lg transition-all duration-300 hover:border-[#e87729]">
                      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{caracteristica.titulo}</h3>
                      <p className="text-gray-600 text-center">{caracteristica.descripcion}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lista de Revistas */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-[#0b742f]" />
                Revistas Administradas por CENID
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Publicaciones científicas especializadas en diversas áreas del conocimiento
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {revistasActivas.map((revista) => (
                  <div key={revista.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-[#0b742f] flex flex-col h-full">
                    <div className={`bg-gradient-to-r ${revista.color} h-48 flex items-center justify-center p-6`}>
                      <div className="text-center text-white">
                        <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-80" />
                        <h3 className="text-lg font-bold">{revista.siglas}</h3>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h4 className="font-bold text-gray-900 mb-3 text-lg leading-tight">
                        {revista.nombre}
                      </h4>
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                        {revista.descripcion}
                      </p>
                      <div className="mt-auto pt-2">
                        <a
                          href={revista.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#0b742f] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#085a24] transition-colors duration-300 w-full justify-center"
                        >
                          Visitar Revista
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Llamada a la acción */}
      <section className="py-16 bg-gradient-to-br from-[#e87729] to-[#d66820] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">{data.cta.titulo}</h2>
            <p className="text-xl text-orange-100 mb-8">
              {data.cta.descripcion}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {data.cta.botones.map((boton, index) => {
                const IconComponent = iconMap[boton.icono];
                const isPrimary = index === 0;

                return (
                  <a
                    key={index}
                    href={boton.url}
                    target={boton.tipo === 'externo' ? '_blank' : undefined}
                    rel={boton.tipo === 'externo' ? 'noopener noreferrer' : undefined}
                    className={`inline-flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-colors duration-300 ${isPrimary
                      ? 'bg-white text-[#e87729] hover:bg-gray-100'
                      : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20'
                      }`}
                  >
                    {IconComponent && <IconComponent className="w-5 h-5" />}
                    {boton.texto}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Información sobre SciELO */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Globe className="w-8 h-8 text-[#0b742f]" />
                {data.scielo.titulo}
              </h2>

              <div className="space-y-6 text-gray-700">
                <p className="text-lg leading-relaxed">
                  {data.scielo.descripcion}
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                  <p className="font-semibold text-blue-800 mb-4">Reconocimiento Internacional:</p>
                  <p className="text-blue-700 mb-4">
                    {data.scielo.reconocimiento}
                  </p>

                  <div className="space-y-2">
                    {data.scielo.beneficios.map((beneficio, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-blue-700">{beneficio}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Información de Contacto */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">{data.contacto.titulo}</h2>
            <p className="text-xl text-green-100 mb-8">
              {data.contacto.descripcion}
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {data.contacto.items.map((item, index) => {
                const IconComponent = iconMap[item.icono];

                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    {IconComponent && <IconComponent className="w-8 h-8 mx-auto mb-3" />}
                    <h3 className="font-semibold mb-2">{item.titulo}</h3>
                    <p className="text-green-100">{item.valor}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Revistas;